/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-argument */
/* eslint-disable @typescript-eslint/explicit-module-boundary-types */

/**
 * Generic hierarchical data structure for animation data.
 *
 * TODO(W-19387372): Replace with generated TypeScript types from key-bundle-compatibility-validator.
 * This interface uses `any` because HUTT JSON structure wasn't formally typed when created.
 * Once W-19387372 is complete, we'll have proper typed access instead of path-based getValue().
 */
export interface IDataStore {
  /**
   * Get a value from the datastore by path.
   *
   * @param path - Dot-separated path (e.g., 'marks.mark_123.x')
   * @returns The value at that path, or undefined if not found
   *
   * @example
   * dataStore.getValue('marks.mark_123.x') // returns 100
   * dataStore.getValue('marks.mark_123.color') // returns 0xFFFF0000
   */
  getValue(path: string): any;

  /**
   * Set a value in the datastore by path.
   * Creates intermediate objects as needed.
   *
   * @param path - Dot-separated path
   * @param value - The value to set
   *
   * @example
   * dataStore.setValue('marks.mark_123.x', 100);
   * dataStore.setValue('marks.mark_123.color', 0xFFFF0000);
   */
  setValue(path: string, value: any): void;

  /**
   * Check if a path exists in the datastore.
   *
   * @param path - Dot-separated path
   * @returns true if the path exists, false otherwise
   */
  hasValue(path: string): boolean;

  /**
   * Get all keys at a given path.
   * For objects, returns property names.
   * For arrays, returns numeric indices as strings.
   *
   * @param path - Dot-separated path
   * @returns Array of keys, or empty array if path doesn't exist
   *
   * @example
   * dataStore.getAllKeys('marks') // ['mark_123', 'mark_456', 'mark_789']
   * dataStore.getAllKeys('marks.mark_123') // ['x', 'y', 'color', 'size']
   */
  getAllKeys(path: string): string[];

  /**
   * Clone this datastore (deep copy).
   * Changes to the clone will not affect the original.
   *
   * @returns A new IDataStore with the same data
   */
  clone(): IDataStore;

  /**
   * Get the underlying data as a plain JavaScript object.
   * Useful for debugging and serialization.
   *
   * @returns The data as a plain object
   */
  toObject(): any;
}

/**
 * Simple implementation of IDataStore backed by a plain JavaScript object.
 *
 * This implementation prioritizes simplicity and compatibility with JSON.
 * Performance optimizations can be added later if needed.
 */
export class DataStore implements IDataStore {
  private data: any;

  /**
   * Create a DataStore from an object or start with an empty store.
   *
   * @param initialData - Optional initial data object
   */
  constructor(initialData: any = {}) {
    this.data = initialData;
  }

  getValue(path: string): any {
    const keys = path.split('.');
    let current = this.data;

    for (const key of keys) {
      if (current === undefined || current === null) {
        return undefined;
      }
      current = current[key];
    }

    return current;
  }

  setValue(path: string, value: any): void {
    const keys = path.split('.');
    let current = this.data;

    // Navigate to the parent of the target key, creating objects as needed
    for (let i = 0; i < keys.length - 1; i++) {
      const key = keys[i];
      if (current[key] === undefined || current[key] === null) {
        current[key] = {};
      }
      current = current[key];
    }

    // Set the final value
    const lastKey = keys[keys.length - 1];
    current[lastKey] = value;
  }

  hasValue(path: string): boolean {
    return this.getValue(path) !== undefined;
  }

  getAllKeys(path: string): string[] {
    const value = this.getValue(path);

    if (value === undefined || value === null) {
      return [];
    }

    if (typeof value !== 'object') {
      return [];
    }

    if (Array.isArray(value)) {
      return value.map((_, index) => index.toString());
    }

    return Object.keys(value);
  }

  clone(): IDataStore {
    // Deep clone - need to handle TypedArrays specially since JSON.stringify converts them to plain objects
    const clonedData = this.deepClone(this.data);
    return new DataStore(clonedData);
  }

  /**
   * Deep clone an object, preserving TypedArrays and regular Arrays.
   * JSON.stringify/parse converts TypedArrays to plain objects, which breaks .slice() calls.
   */
  private deepClone(obj: any): any {
    // Handle null/undefined
    if (obj === null || obj === undefined) {
      return obj;
    }

    // Handle primitives
    if (typeof obj !== 'object') {
      return obj;
    }

    // Handle TypedArrays - create new instance of same type
    if (ArrayBuffer.isView(obj)) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const typedArray = obj as any;
      // Create new typed array of same type and copy data
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      const constructor = typedArray.constructor;
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-return
      return new constructor(typedArray);
    }

    // Handle regular Arrays
    if (Array.isArray(obj)) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return
      return obj.map((item: any) => this.deepClone(item));
    }

    // Handle plain objects
    const cloned: any = {};
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        cloned[key] = this.deepClone(obj[key]);
      }
    }
    return cloned;
  }

  toObject(): any {
    return this.data;
  }
}
